import { useEffect, useState } from "react";
import styled from "styled-components";
import media from "styled-media-query";
import { ProductScheme, EventScheme } from "client-v2";
import dayjs from "dayjs";
import "dayjs/locale/es";

import { Text, ColorV2 } from "@adoptaunabuelo/react-components";
import * as icons from "lucide-react";
import Map from "ol/Map";
import View from "ol/View";
import Timing from "../../../components/cell/Timing";
import "ol/ol.css";
import { fromLonLat } from "ol/proj";
import { Feature, Collection } from "ol";
import { Point } from "ol/geom";
import { Icon, Style } from "ol/style";
import { Vector as VectorSource, XYZ } from "ol/source";
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";

const Container = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
`;
const MobileSection = styled.div`
	display: none;
	flex: 1;
	flex-direction: column;
	gap: 24px;
	padding: 24px 0px;
	margin-top: 24px;
	border-top: 1px solid ${ColorV2.border.neutralSoft};
	border-bottom: 1px solid ${ColorV2.border.neutralSoft};
	${media.lessThan("medium")`
        display: flex;
    `}
`;
const Section = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	padding: 32px 0px;
	border-top: 1px solid ${ColorV2.border.neutralSoft};
	${media.lessThan("medium")`
        padding: 24px 0px;
    `}
`;
const BulletContainer = styled.div`
	display: flex;
	flex: 1;
	flex-direction: row;
	gap: 16px;
	flex-wrap: wrap;
	${media.lessThan("medium")`
        gap: 8px;
    `}
`;
const BulletCell = styled.div`
	display: flex;
	flex-direction: column;
	max-width: 160px;
	min-width: 160px;
	flex: 1;
	padding: 16px;
	border-radius: 12px;
	background-color: ${ColorV2.surface.background};
	${media.lessThan("medium")`
        
    `}
	${media.lessThan("small")`
        flex: 2;
        min-width: 30%;
    `}
`;
const MapView = styled.div`
	position: relative;
	height: 356px;
	border-radius: 12px;
	overflow: hidden;
	margin-top: 32px;
`;
const Row = styled.div`
	display: flex;
	flex-direction: row;
`;
const Column = styled.div`
	display: flex;
	flex-direction: column;
`;
const CheckIcon = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 24px;
	width: 24px;
	border-radius: 30px;
	margin-right: 12px;
`;

const Info = (props: Props) => {
	const iconsT: any = icons;
	const Check = iconsT["Check"];
	const [pastDue, setPastDue] = useState(false);

	useEffect(() => {
		const pastDue = props.product.startDate
			? dayjs() > dayjs(props.product.startDate.iso)
			: false;
		setPastDue(pastDue);
	}, []);

	useEffect(() => {
		//Create the map tile layer
		const lightLayer = new TileLayer({
			source: new XYZ({
				url: "https://{a-d}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
				attributions: "© CartoDB © OpenStreetMap",
			}),
		});
		//Create the marker
		const iconFeature = new Feature({
			geometry: new Point(
				fromLonLat([
					props.event.location.longitude,
					props.event.location.latitude,
				])
			),
			name: "Madrid",
		});
		iconFeature.setStyle(
			new Style({
				image: new Icon({
					src: "https://adoptaunabuelo.org/wp-content/uploads/2025/06/map_pin.png",
					scale: 0.5,
				}),
			})
		);

		//Create the vector layer
		const vectorSource = new VectorSource({
			features: [iconFeature],
		});

		const vectorLayer = new VectorLayer({
			source: vectorSource,
		});

		const map = new Map({
			target: "map",
			layers: [lightLayer, vectorLayer],
			view: new View({
				center: fromLonLat([
					props.event.location.longitude,
					props.event.location.latitude,
				]),
				zoom: 16,
			}),
			interactions: new Collection(), 
		});

		return () => {
			map.setTarget(undefined);
		};
	}, []);

	return (
		<Container>
			<MobileSection>
				<Row style={{ gap: 12 }}>
					<icons.Calendar height={24} width={24} />
					<Column>
						<Text type="p" weight="medium">
							{dayjs(props.product.startDate?.iso).locale("es").format(
								"dddd, D [de] MMMM [de] YYYY"
							)}
						</Text>
						<Text
							type="p2"
							style={{ color: ColorV2.text.neutralMedium }}
						>
							de{" "}
							{dayjs(props.product.startDate?.iso).format(
								"HH:mm"
							)}{" "}
							a{" "}
							{dayjs(props.product.endDate?.iso).format("HH:mm")}
						</Text>
					</Column>
				</Row>
				<Row style={{ gap: 12 }}>
					<icons.MapPin height={24} width={24} />
					<Column>
						<Text type="p" weight="medium">
							{props.event.shortAddress}
						</Text>
						<Text
							type="p2"
							style={{ color: ColorV2.text.neutralMedium }}
						>
							{props.event.address}
						</Text>
					</Column>
				</Row>
			</MobileSection>
			<Section>
				<Text type="h4" weight="semibold" style={{ marginBottom: 24 }}>
					Descripción del evento
				</Text>
				<Text type="p" style={{ whiteSpace: "pre-wrap" }}>
					{props.event.description}
				</Text>
			</Section>
			{props.event.timeline && (
				<Section>
					<Text
						type="h4"
						weight="semibold"
						style={{ marginBottom: 24 }}
					>
						Programación
					</Text>
					<Timing data={props.event.timeline} />
				</Section>
			)}
			{props.event.bullet && (
				<Section>
					<Text
						type="h4"
						weight="semibold"
						style={{ marginBottom: 24 }}
					>
						Qué incluye
					</Text>
					<BulletContainer>
						{props.event.bullet.map((item, index: number) => {
							const Icon = iconsT[item.icon];
							return (
								<BulletCell key={"bullet-" + index}>
									<Icon style={{ marginBottom: 12 }} />
									<Text type="p" weight="medium">
										{item.title}
									</Text>
									<Text
										type="c1"
										style={{
											color: ColorV2.text.neutralMedium,
										}}
									>
										{item.subtitle}
									</Text>
								</BulletCell>
							);
						})}
					</BulletContainer>
				</Section>
			)}
			<Section>
				<Text type="h4" weight="semibold" style={{ marginBottom: 4 }}>
					¿Dónde es?
				</Text>
				<Text type="p">{props.event.address}</Text>
				<MapView id="map" />
			</Section>
			{(!props.product.data || !props.product.data.corporate) && (
				<Section>
					<Text
						type="h4"
						weight="semibold"
						style={{ marginBottom: 24 }}
					>
						Ten en cuenta
					</Text>
					<Column>
						<Text
							type="h5"
							weight="medium"
							style={{ marginBottom: 16 }}
						>
							Requisitos para los asistentes
						</Text>
						<Row style={{ marginBottom: 16 }}>
							<CheckIcon>
								<Check
									color={ColorV2.text.primary}
									height={18}
									width={18}
									strokeWidth={3}
								/>
							</CheckIcon>
							<Text type="p" style={{ flex: 1 }}>
								Los menores de 16 años tendrán que ir
								acompañados por padres o tutores.
							</Text>
						</Row>
						<Row style={{ marginBottom: 16 }}>
							<CheckIcon>
								<Check
									color={ColorV2.text.primary}
									height={18}
									width={18}
									strokeWidth={3}
								/>
							</CheckIcon>
							<Text type="p" style={{ flex: 1 }}>
								Si el evento es en residencia se aplicarán las
								medidas de seguridad de la residencia.
							</Text>
						</Row>
						<Row>
							<CheckIcon>
								<Check
									color={ColorV2.text.primary}
									height={18}
									width={18}
									strokeWidth={3}
								/>
							</CheckIcon>
							<Text type="p" style={{ flex: 1 }}>
								Informa a Adopta Un Abuelo sobre cualquier
								alergia o restricción alimentaria.
							</Text>
						</Row>
						{props.product.price > 0 && (
							<>
								<Text
									type="h5"
									weight="medium"
									style={{ marginBottom: 16, marginTop: 24 }}
								>
									Política de cancelación
								</Text>
								<Text type="p">
									Cancela hasta 48 horas antes de que empiece
									la experiencia para obtener el reembolso
									completo.
								</Text>
							</>
						)}
					</Column>
				</Section>
			)}
		</Container>
	);
};
export default Info;
export interface Props {
	product: ProductScheme;
	event: EventScheme;
}
